import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileImage, Loader2, Minus, Plus, ShoppingBag, Truck, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SavedAddressPicker } from '@/components/saved-address-picker';
import { AuthImage } from '@/components/auth-image';
import { ApiError } from '@/lib/api';
import { form_error_message } from '@/lib/forms';
import { RegionBlockCallout, as_region_block } from '@/components/region-block-callout';
import { use_cart, FREE_DELIVERY_THRESHOLD_INR } from '@/features/commerce/cart';
import {
  use_place_commerce_order,
  use_pay_commerce_order,
  use_verify_commerce_payment,
} from '@/features/commerce/hooks';
import { open_razorpay_checkout } from '@/features/booking/razorpay-checkout';

/**
 * Floating cart bar + checkout sheet for the cross-vendor commerce cart (FOOD or PHARMACY
 * products). Lines are grouped by vendor; delivery is always to an address (no pickup, unlike
 * the legacy pharmacy cart) and the delivery fee is a single whole-order charge. A PHARMACY
 * cart with any Rx line requires a prescription image upload, same as the legacy sheet.
 */
export function CommerceCartSheet() {
  const navigate = useNavigate();
  const cart = use_cart();
  // Every number below is the server's, not ours — the Pay button and the charge
  // are the same arithmetic by construction.
  const { item_count: count, goods_total_inr, delivery_fee_inr, grand_total_inr } = cart.cart;
  const goods_total = Number(goods_total_inr);
  const delivery_fee = Number(delivery_fee_inr);
  const grand_total = Number(grand_total_inr);
  const has_rx = cart.cart.prescription_required;
  // `?cart=open` (from a product page's "Go to checkout") opens the cart straight away.
  const [search_params] = useSearchParams();
  const [open, set_open] = useState(() => search_params.get('cart') === 'open');
  const [phone, set_phone] = useState('');
  const [saved_address_id, set_saved_address_id] = useState<string | undefined>(undefined);
  const [address, set_address] = useState('');
  const [landmark, set_landmark] = useState('');
  const [city, set_city] = useState('');
  const [pincode, set_pincode] = useState('');
  const [notes, set_notes] = useState('');
  const [files, set_files] = useState<File[]>([]);
  const file_input = useRef<HTMLInputElement>(null);
  const place_m = use_place_commerce_order();
  const pay_m = use_pay_commerce_order();
  const verify_m = use_verify_commerce_payment();
  const [pay_error, set_pay_error] = useState<string | null>(null);

  if (count === 0) return null;
  const lines = cart.cart.items;
  const verticals = [...new Set(lines.map((l) => l.vertical))];

  // Group lines by vendor to preview per-vendor shipments. Vendors are hidden from the shopper,
  // so groups are labelled by an ordinal ("Shipment N") rather than a vendor name.
  const by_vendor = new Map<string, { lines: typeof lines }>();
  for (const l of lines) {
    const g = by_vendor.get(l.vendor_profile_id);
    if (g) g.lines.push(l);
    else by_vendor.set(l.vendor_profile_id, { lines: [l] });
  }
  const shipment_count = by_vendor.size;

  function finish() {
    // Checkout already emptied the server cart; just re-read so the bar disappears.
    cart.refresh();
    set_files([]);
    set_open(false);
    navigate('/app/orders');
  }

  function handle_files(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = ''; // allow re-selecting the same file
    if (picked.length === 0) return;
    set_files((f) => [...f, ...picked]);
  }

  function remove_file(idx: number) {
    set_files((f) => f.filter((_, i) => i !== idx));
  }

  // "Pet food", "Pharmacy", or "Pet food & Pharmacy order" for a mixed cart.
  const service_name =
    verticals.length > 1
      ? 'Pet food & Pharmacy order'
      : verticals[0] === 'PHARMACY'
        ? 'Pharmacy order'
        : 'Food order';

  async function place() {
    set_pay_error(null);
    try {
      const order = await place_m.mutateAsync({
        input: {
          contact_phone: phone.trim(),
          saved_address_id,
          address: address.trim() || undefined,
          landmark: landmark.trim() || undefined,
          city: city.trim() || undefined,
          pincode: pincode.trim() || undefined,
          notes: notes.trim() || undefined,
          // No items[]: the order is the server's own cart. Sending our copy would
          // reintroduce the client as a source of truth for what's being bought.
          // Whatever the Pay button says is what we're authorising. If a vendor re-priced an
          // item since it went in the cart, the server rejects with 422 PRICE_CHANGED rather
          // than charging the new amount silently.
          expected_grand_total_inr: grand_total,
        },
        files,
      });

      // The order sits in PENDING_PAYMENT until we capture payment. Razorpay when a
      // checkout descriptor is present; the dummy gateway (dev/test) otherwise.
      if (order.checkout) {
        await open_razorpay_checkout({
          checkout: order.checkout,
          service_name,
          contact: phone.trim(),
          on_success: async (resp) => {
            try {
              await verify_m.mutateAsync({
                id: order.id,
                input: {
                  razorpay_order_id: resp.razorpay_order_id,
                  razorpay_payment_id: resp.razorpay_payment_id,
                  razorpay_signature: resp.razorpay_signature,
                },
              });
              finish();
            } catch (err) {
              set_pay_error(form_error_message(err, 'Payment verification failed. If charged, contact support.'));
            }
          },
          // Don't promise a screen that doesn't exist: My Orders has no way to resume
          // payment, so the honest instruction is to re-order (BUG-039).
          on_dismiss: () =>
            set_pay_error('Payment cancelled — nothing has been charged. Your cart is still here, so you can pay again whenever you are ready.'),
          on_failure: (message) => set_pay_error(message),
        });
      } else {
        await pay_m.mutateAsync(order.id);
        finish();
      }
    } catch (err) {
      set_pay_error(form_error_message(err, 'Could not complete payment. Please try again.'));
    }
  }

  const paying = place_m.isPending || pay_m.isPending || verify_m.isPending;
  const rx_satisfied = !has_rx || files.length > 0;
  // A line the catalog can no longer sell would make checkout fail server-side;
  // block it here with a reason instead of a 422 after the payment sheet opens.
  const blocked_by_unorderable = cart.cart.has_unorderable_items;
  const can_place =
    phone.trim().length >= 8 && address.trim().length > 0 && rx_satisfied && !paying && !blocked_by_unorderable;

  const region_block = as_region_block(place_m.error);

  const error_message =
    region_block
      ? null
      : place_m.error instanceof ApiError && place_m.error.error_code === 'PRESCRIPTION_REQUIRED'
      ? 'A prescription image is required for the Rx item(s) in your cart.'
        : place_m.error
          ? form_error_message(place_m.error, 'Could not place the order.')
          : blocked_by_unorderable
            ? 'One or more items in your cart can no longer be ordered. Remove them to continue.'
            : pay_error;

  return (
    <>
      {/* Floating bar */}
      <button
        type="button"
        onClick={() => set_open(true)}
        data-testid="commerce-cart-bar"
        className="fixed inset-x-0 bottom-4 z-40 mx-auto flex w-[min(36rem,calc(100%-2rem))] items-center justify-between gap-3 rounded-card accent-gradient px-5 py-3 text-primary-foreground shadow-clay-lg transition-transform active:scale-[0.98]"
      >
        <span className="flex items-center gap-2 font-semibold">
          <ShoppingBag className="h-5 w-5" /> {count} item{count > 1 ? 's' : ''}
        </span>
        <span className="font-heading text-lg font-bold tabular-nums">₹{grand_total} · View cart</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          data-testid="commerce-cart-sheet"
          onClick={() => set_open(false)}
        >
          <div
            className="max-h-[90vh] w-full overflow-y-auto rounded-t-card border-2 border-brand-outline-variant/20 bg-card shadow-clay-lg sm:max-w-md sm:rounded-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-border p-4">
              <h2 className="font-heading text-lg font-bold">Your order</h2>
              <button
                type="button"
                onClick={() => set_open(false)}
                aria-label="Close"
                className="rounded-full p-2 hover:bg-muted"
                data-testid="commerce-cart-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex flex-col divide-y-2 divide-brand-outline-variant/10">
              {[...by_vendor.entries()].map(([vendor_id, g], idx) => (
                <div key={vendor_id} data-testid={`commerce-cart-vendor-${vendor_id}`}>
                  {shipment_count > 1 && (
                    <h3 className="px-4 pt-3 font-heading text-sm font-bold text-muted-foreground">
                      Shipment {idx + 1} of {shipment_count}
                    </h3>
                  )}
                  <ul className="divide-y divide-brand-outline-variant/10 p-2">
                    {g.lines.map((l) => (
                      <li
                        key={l.variant_id}
                        className="flex items-center gap-3 p-2"
                        data-testid={`commerce-cart-line-${l.variant_id}`}
                      >
                        {/* Thumbnail: the shopper picked this by sight on the shop
                            screen, so a text-only row makes them re-read the name. */}
                        <div className="h-11 w-11 shrink-0 overflow-hidden rounded-input bg-secondary/40">
                          {l.images.length > 0 ? (
                            <AuthImage
                              path={l.images[0].thumb_path}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-lg">🐾</div>
                          )}
                        </div>
                        <span className="min-w-0 flex-1 truncate font-medium">
                          {l.name}
                          <span className="ml-1.5 text-xs text-muted-foreground">{l.variant_label}</span>
                          {l.is_prescription_required && (
                            <span className="ml-1.5 text-xs font-semibold text-warning">Rx</span>
                          )}
                          {!l.is_available && (
                            <span
                              className="ml-1.5 text-xs font-semibold text-destructive"
                              data-testid={`commerce-cart-unavailable-${l.variant_id}`}
                            >
                              {l.unavailable_reason === 'REMOVED' ? 'No longer sold' : 'Out of stock'} — remove to
                              continue
                            </span>
                          )}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => cart.set_qty(l.variant_id, l.quantity - 1)}
                            aria-label="Decrease"
                            className="rounded-full bg-secondary p-1 active:scale-90"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-5 text-center tabular-nums">{l.quantity}</span>
                          <button
                            type="button"
                            onClick={() => cart.inc(l.variant_id)}
                            aria-label="Increase"
                            disabled={!l.is_available}
                            className="rounded-full bg-primary/10 p-1 text-primary active:scale-90 disabled:opacity-40"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                        <span className="w-20 shrink-0 text-right tabular-nums">
                          <span className="font-semibold">₹{l.line_total_inr}</span>
                          {l.mrp_inr && l.discount_pct != null && (
                            <span className="block text-[11px] leading-tight text-muted-foreground">
                              <span className="line-through">₹{Number(l.mrp_inr) * l.quantity}</span>
                              <span className="ml-1 font-semibold text-success">-{l.discount_pct}%</span>
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3 p-4">
              <div className="flex flex-col gap-1 rounded-input border-2 border-brand-outline-variant/15 bg-secondary/30 p-3 text-sm" data-testid="commerce-cart-totals">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Goods total</span>
                  <span className="tabular-nums">₹{goods_total}</span>
                </div>
                <div className="flex justify-between">
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Truck className="h-3.5 w-3.5" /> Delivery fee
                  </span>
                  <span className="tabular-nums" data-testid="commerce-cart-delivery-fee">
                    {delivery_fee === 0 ? (
                      <span className="font-semibold text-success">Free</span>
                    ) : (
                      `₹${delivery_fee}`
                    )}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">Free delivery over ₹{FREE_DELIVERY_THRESHOLD_INR}</p>
                {/* Client change (9 Oct 2026): expected delivery time. */}
                {cart.cart.estimated_delivery && (
                  <div className="flex justify-between" data-testid="commerce-cart-eta">
                    <span className="text-muted-foreground">Expected delivery</span>
                    <span className="font-semibold">{cart.cart.estimated_delivery.label}</span>
                  </div>
                )}
                <div className="mt-1 flex justify-between border-t border-brand-outline-variant/15 pt-1.5 font-heading font-bold">
                  <span>Grand total</span>
                  <span className="tabular-nums">₹{grand_total}</span>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="commerce-cart-phone">Contact phone</Label>
                <Input
                  id="commerce-cart-phone"
                  value={phone}
                  onChange={(e) => set_phone(e.target.value)}
                  placeholder="+91…"
                  data-testid="commerce-cart-phone"
                />
              </div>

              <SavedAddressPicker
                on_select={(a) => {
                  set_saved_address_id(a.id);
                  set_address(a.address);
                  set_landmark(a.landmark ?? '');
                  set_city(a.city ?? '');
                  set_pincode(a.pincode ?? '');
                  if (a.phone) set_phone(a.phone);
                }}
                on_clear={() => {
                  set_saved_address_id(undefined);
                  set_address('');
                  set_landmark('');
                  set_city('');
                  set_pincode('');
                }}
              />
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="commerce-cart-address">Delivery address</Label>
                <Input
                  id="commerce-cart-address"
                  value={address}
                  onChange={(e) => {
                    set_saved_address_id(undefined);
                    set_address(e.target.value);
                  }}
                  placeholder="House, street, area"
                  data-testid="commerce-cart-address"
                />
              </div>
              <div className="flex gap-2">
                <Input
                  value={landmark}
                  onChange={(e) => set_landmark(e.target.value)}
                  placeholder="Landmark (optional)"
                  data-testid="commerce-cart-landmark"
                />
              </div>
              <div className="flex gap-2">
                <Input value={city} onChange={(e) => set_city(e.target.value)} placeholder="City" data-testid="commerce-cart-city" />
                <Input
                  value={pincode}
                  onChange={(e) => set_pincode(e.target.value)}
                  placeholder="Pincode"
                  className="w-32"
                  data-testid="commerce-cart-pincode"
                />
              </div>

              <Input
                value={notes}
                onChange={(e) => set_notes(e.target.value)}
                placeholder="Notes (optional)"
                data-testid="commerce-cart-notes"
              />

              {has_rx && (
                <div
                  className="flex flex-col gap-2 rounded-input border-2 border-warning/30 bg-warning/10 p-3"
                  data-testid="commerce-rx-notice"
                >
                  <p className="text-sm font-semibold text-warning">
                    Your cart has a prescription item — attach a photo of the prescription to place this order.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="self-start"
                    onClick={() => file_input.current?.click()}
                    data-testid="commerce-prescription-trigger"
                  >
                    <FileImage className="h-4 w-4" /> Attach prescription
                  </Button>
                  {files.length > 0 && (
                    <ul className="flex flex-col gap-1">
                      {files.map((f, i) => (
                        <li
                          key={`${f.name}-${i}`}
                          className="flex items-center justify-between gap-2 rounded-input bg-card px-2 py-1 text-xs"
                          data-testid={`commerce-prescription-file-${i}`}
                        >
                          <span className="min-w-0 flex-1 truncate">{f.name}</span>
                          <button
                            type="button"
                            onClick={() => remove_file(i)}
                            aria-label="Remove attachment"
                            className="shrink-0 text-muted-foreground hover:text-destructive"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <input
                ref={file_input}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                data-testid="commerce-prescription-input"
                onChange={handle_files}
              />

              <p className="rounded-input border-2 border-dashed border-brand-outline-variant/30 p-2 text-center text-xs text-muted-foreground">
                Courier charges are borne by the vendor and settled at delivery. Pay now to confirm — vendors see your order
                only once payment succeeds.
              </p>

              {region_block && <RegionBlockCallout error={region_block} />}

              {error_message && (
                <p className="text-sm text-destructive" data-testid="commerce-cart-error">
                  {error_message}
                </p>
              )}

              <Button variant="gradient" disabled={!can_place} onClick={place} data-testid="commerce-place-order">
                {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Pay ₹{grand_total}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
